import {Stack, Text, Title} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import type {TagSchema} from 'constants/RegistryConstants';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

import type {ModalAction, ModalState} from './moleculeImportReducer';
import useMoleculeSchemasPairing from './useMoleculeSchemasPairing';

interface ConfigurePaneProps {
  state: ModalState;
  dispatch: React.Dispatch<ModalAction>;
}

export default function ConfigurePane({state, dispatch}: ConfigurePaneProps): JSX.Element {
  const {pairingMap} = useMoleculeSchemasPairing(null);
  const [warningDismissed, setWarningDismissed] = useState(false);

  const handleSchemaChange = useCallback(
    (tagSchema: TagSchema | null) => {
      setWarningDismissed(false);
      if (!tagSchema) {
        dispatch({type: 'SET_SCHEMA', payload: {schemaId: null, compoundSchemaId: null}});
        dispatch({type: 'SET_REGISTRY_ID', payload: {registryId: null}});
        dispatch({type: 'SET_SCHEMA_LABELING_STRATEGIES', payload: {labelingStrategies: null}});
        return;
      }
      const pairing = pairingMap[tagSchema.id];
      dispatch({
        type: 'SET_SCHEMA',
        payload: {
          schemaId: tagSchema.id,
          compoundSchemaId: pairing?.compoundSchemaId ?? null,
        },
      });
      dispatch({type: 'SET_REGISTRY_ID', payload: {registryId: tagSchema.registryId}});
      dispatch({
        type: 'SET_SCHEMA_LABELING_STRATEGIES',
        payload: {labelingStrategies: tagSchema.labelingStrategies},
      });
    },
    [dispatch, pairingMap]
  );

  const getSchemaMetadata = (schema: {id: string}): string | undefined => {
    const pairing = pairingMap[schema.id];
    if (!pairing) {
      return undefined;
    }
    if (schema.id === pairing.formSchemaId) {
      return `Linked to: ${pairing.compoundSchemaName}`;
    }
    return `Linked to: ${pairing.formSchemaName}`;
  };

  const schemaBanner = useMemo(() => {
    if (!state.schemaId) {
      return null;
    }
    const pairing = pairingMap[state.schemaId];
    if (!pairing) {
      if (warningDismissed) {
        return null;
      }
      return {
        type: InlineBannerType.WARNING,
        text: 'The schema you selected does not support the import of salt or solvate molecules.',
        supportingInfo:
          'Salt or solvate molecules in your upload will fail to be imported with the schema you selected. Select a different schema, or continue to upload.',
        dismissible: true,
      };
    }
    const linkedSchemaName =
      state.schemaId === pairing.formSchemaId ? pairing.compoundSchemaName : pairing.formSchemaName;
    return {
      type: InlineBannerType.INFO,
      text: 'This schema supports both standard molecules and salted or solvate forms.',
      supportingInfo: `All molecule types in your upload will be created using this schema or its linked ${linkedSchemaName}.`,
      dismissible: false,
    };
  }, [state.schemaId, pairingMap, warningDismissed]);

  return (
    <Stack spacing="md">
      <Stack spacing="xxs">
        {/* m={0} neutralizes the global Bootstrap <h5> margin so the Stack's xxs gap governs spacing. */}
        <Title order={5} m={0}>
          Select a schema
        </Title>
        <Text size="md">Molecule entities will be created based on the schema you select.</Text>
      </Stack>
      <TagSchemaSelector
        oligoAwareFolderItemTypes={[ModelTypeConstants.MOLECULE]}
        selectedTagSchemaId={state.schemaId}
        onChange={handleSchemaChange}
        getSchemaMetadata={getSchemaMetadata}
      />

      {schemaBanner && (
        <InlineBanner
          bannerType={schemaBanner.type}
          text={schemaBanner.text}
          supportingInfo={schemaBanner.supportingInfo}
          onDismiss={
            schemaBanner.dismissible
              ? () => {
                  setWarningDismissed(true);
                }
              : undefined
          }
        />
      )}
      {!state.schemaId && (
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text="Some schemas may not support all molecule types, such as salts and solvates."
          supportingInfo="If your upload includes these, make sure to select a schema that supports them. If you're unsure of which schema to select, consult with your administrator."
        />
      )}
    </Stack>
  );
}
